import axios from 'axios';

window.axios = axios;
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
window.axios.defaults.withCredentials = true;

// Function to get CSRF token from meta tag
const getCsrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

// Function to update CSRF token in meta tag and axios defaults
const updateCsrfToken = (token) => {
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta) {
        meta.content = token;
    }
    window.axios.defaults.headers.common['X-CSRF-TOKEN'] = token;
};

// Set initial CSRF token
window.axios.defaults.headers.common['X-CSRF-TOKEN'] = getCsrfToken();

const syncCsrfTokenHeader = (config) => {
    const token = getCsrfToken();

    if (token) {
        config.headers = config.headers || {};
        config.headers['X-CSRF-TOKEN'] = token;
    }

    return config;
};

// Flag to prevent multiple simultaneous CSRF refreshes
let isRefreshingCsrf = false;
let csrfRefreshPromise = null;

// Function to refresh CSRF token via Sanctum
const refreshCsrfToken = async () => {
    if (isRefreshingCsrf) {
        return csrfRefreshPromise;
    }

    isRefreshingCsrf = true;
    csrfRefreshPromise = axios.get('/sanctum/csrf-cookie', {
        // Skip the interceptor for this request to avoid infinite loop
        _skipCsrfRefresh: true
    }).then(() => {
        // After getting the cookie, extract token from the XSRF-TOKEN cookie
        const xsrfToken = document.cookie
            .split('; ')
            .find(row => row.startsWith('XSRF-TOKEN='))
            ?.split('=')[1];

        if (xsrfToken) {
            // Decode the URI-encoded token
            const decodedToken = decodeURIComponent(xsrfToken);
            updateCsrfToken(decodedToken);
        }
        return true;
    }).catch((error) => {
        console.error('Failed to refresh CSRF token:', error);
        return false;
    }).finally(() => {
        isRefreshingCsrf = false;
        csrfRefreshPromise = null;
    });

    return csrfRefreshPromise;
};

const installCsrfInterceptors = (client) => {
    client.interceptors.request.use(syncCsrfTokenHeader);

    client.interceptors.response.use(
        (response) => {
            return response;
        },
        async (error) => {
            const originalRequest = error.config;
            const status = error?.response?.status;

            // Handle CSRF token mismatch (419)
            if (status === 419 && !originalRequest._skipCsrfRefresh && !originalRequest._csrfRetried) {
                originalRequest._csrfRetried = true;

                try {
                    console.warn('CSRF token expired, refreshing...');
                    const refreshed = await refreshCsrfToken();

                    if (refreshed) {
                        // Update the original request with the new token
                        originalRequest.headers['X-CSRF-TOKEN'] = getCsrfToken();
                        return axios(originalRequest);
                    }
                } catch (refreshError) {
                    console.error('Failed to refresh CSRF token:', refreshError);
                }

                // If refresh failed, reload the page
                console.warn('CSRF refresh failed, reloading page...');
                window.location.reload();
                return new Promise(() => {}); // Never resolve to prevent further processing
            }

            // Handle session expiry (401) - reload page
            if (status === 401) {
                console.warn('Session expired, reloading…');
                window.location.reload();
                return new Promise(() => {}); // Never resolve
            }

            return Promise.reject(error);
        }
    );
};

installCsrfInterceptors(axios);

const teamAxios = axios.create({
    params: {
        _tsr: window._tsr,
    },
});

installCsrfInterceptors(teamAxios);

export const callWithTeamConfig = function () {
    return teamAxios;
};

export const addTeamSessionParams = (url) => {
    const separator = url.includes('?') ? '&' : '?';
    return url + separator + '_tsr=' + window._tsr;
}
